import { Component, ContentChild, Directive, EventEmitter, Input, Optional, Output, TemplateRef } from '@angular/core';
import { ModalComponent } from '../modal.component';
import { ButtonColor } from '@backbase/ui-ang/button';

/**
 * Directive for custom modal footer content template.
 *
 * @remarks
 * Allows embedding custom content within modal footer using Angular's content projection.
 * The directive wraps template content and provides access to the template reference for rendering.
 *
 * @example
 *
 * ```html
 * <bb-modal-footer-ui>
 *   <ng-template bbCustomModalFooter>
 *     Custom footer content with buttons
 *   </ng-template>
 * </bb-modal-footer-ui>
 * ```
 */
@Directive({
  selector: 'ng-template[bbCustomModalFooter]',
  standalone: false,
})
export class CustomModalFooterDirective {
  constructor(public templateRef: TemplateRef<any>) {}
}

/**
 * Modal footer component that provides action buttons and custom content for modal dialogs.
 *
 * @remarks
 * Handles modal footer with confirm and cancel buttons, supporting custom text and event handling.
 * Can work independently or integrate with parent modal component for automatic modal management.
 * Supports custom footer templates through content projection.
 *
 * @example
 *
 * ```html
 * <bb-modal-footer-ui confirmText="Accept" (confirm)="saveAccept()">
 * </bb-modal-footer-ui>
 * ```
 */
@Component({
  selector: 'bb-modal-footer-ui',
  templateUrl: './modal-footer.component.html',
  standalone: false,
})
export class ModalFooterComponent {
  /**
   * Custom modal footer directive containing user-provided template content.
   */
  @ContentChild(CustomModalFooterDirective, { static: true }) customModalFooter: CustomModalFooterDirective | undefined;

  /**
   * Reference to this component instance for template context.
   */
  readonly hostRef: ModalFooterComponent = this;

  constructor(@Optional() private readonly modal: ModalComponent) {}
  /**
   * The text of the confirm button.
   *
   * @example
   *
   * ```html
   * <bb-modal-footer-ui confirmText="Accept" (confirm)="saveAccept()">
   * </bb-modal-footer-ui>
   * ```
   */
  @Input() confirmText?: string;
  /**
   * The color of the confirm button.
   *
   * @example
   *
   * ```html
   * <bb-modal-footer-ui confirmButtonColor="danger" (confirm)="confirmDelete()" />
   * ```
   */
  @Input() confirmButtonColor: ButtonColor = 'primary';
  /**
   * The text of the cancel button.
   *
   * @example
   *
   * ```html
   * <bb-modal-footer-ui cancelText="Close" (cancel)="onCancel()">
   * </bb-modal-footer-ui>
   * ```
   */
  @Input() cancelText?: string;
  /**
   * The color of the cancel button.
   *
   * @example
   *
   * ```html
   * <bb-modal-footer-ui cancelButtonColor="danger" (cancel)="onCancel()" />
   * ```
   */
  @Input() cancelButtonColor: ButtonColor = 'secondary';
  /**
   * The event that's fired after confirm button is pressed. Can be used with (click)="dialogRef.onConfirm()"
   * where dialogRef is a templateRef on <bb-dialog-ui #dialogRef />
   *
   * @example
   *
   * ```html
   * <bb-modal-footer-ui (confirm)="saveAccept()">
   * </bb-modal-footer-ui>
   * ```
   */
  @Output() confirm = new EventEmitter();
  /**
   * The event that's fired after cancel button is pressed. Can be used with (click)="dialogRef.onCancel()"
   * where dialogRef is a templateRef on <bb-dialog-ui #dialogRef />
   *
   * @example
   *
   * ```html
   * <bb-modal-footer-ui (cancel)="onCancel()">
   * </bb-modal-footer-ui>
   * ```
   */
  @Output() cancel = new EventEmitter();

  /**
   * Handles cancel action by dismissing modal or emitting cancel event.
   *
   * @remarks
   * If used within a modal component, automatically dismisses the modal.
   * Otherwise, emits the cancel event for parent component handling.
   *
   * @param $event - Optional event data to pass
   *
   * @example
   *
   * ```html
   * <button (click)="onCancel()">Cancel</button>
   * ```
   */
  onCancel($event?: any) {
    if (this.modal) {
      this.modal.dismissModal($event);
    } else {
      this.cancel.emit($event);
    }
  }

  /**
   * Handles confirm action by closing modal or emitting confirm event.
   *
   * @remarks
   * If used within a modal component, automatically closes the modal.
   * Otherwise, emits the confirm event for parent component handling.
   *
   * @param $event - Optional event data to pass
   *
   * @example
   *
   * ```html
   * <button (click)="onConfirm()">Confirm</button>
   * ```
   */
  onConfirm($event?: any) {
    if (this.modal) {
      this.modal.closeModal($event);
    } else {
      this.confirm.emit($event);
    }
  }
}
